import { concat, EMPTY, from, merge, Observable, of } from 'rxjs'
import { filter, map, mergeMap, pluck } from 'rxjs/operators'
import { tag } from 'rxjs-spy/operators/tag'
import { Epic, StateObservable } from 'redux-observable'
import { isOfType } from 'typesafe-actions'
import { AnyAction } from 'redux'
import { CallHistoryMethodAction, goBack } from 'connected-react-router'
import { EpicDependencies, RootState } from 'src/state/types'
import { agencyApiEvents, agencyApiSelectors } from 'modules/api/agency'
import { AgencyEvent } from 'modules/api/agency/state'
import { TAgencySegment } from 'modules/api/agency/types'
import { ENDPOINTS as AGENCY_ENDPOINTS } from 'modules/api/agency/constants'
import { userApiEvents } from 'modules/api/user'
import { UserEvent } from 'modules/api/user/state'
import { ENDPOINTS as USER_ENDPOINTS } from 'modules/api/user/constants'
import { portfolioApiEvents } from 'modules/api/portfolio'
import { PortfolioEvent } from 'modules/api/portfolio/state'
import { ENDPOINTS as PORTFOLIO_ENDPOINTS } from 'modules/api/portfolio/constants'
import { invoiceApiEvents } from 'modules/api/invoice'
import { InvoiceEvent } from 'modules/api/invoice/state'
import { reconApiConstants, reconApiEvents } from 'modules/api/recon'
import { ReconEvent } from 'modules/api/recon/state'
import { bookSegmentsEvents as events, BookSegmentsEvent } from './state'
import { getDefaultSegmentId } from './selectors'

type TBookSegmentsEpicEvent =
  | BookSegmentsEvent
  | AgencyEvent
  | UserEvent
  | PortfolioEvent
  | InvoiceEvent
  | ReconEvent
  | CallHistoryMethodAction

type BookSegmentsEpic = Epic<TBookSegmentsEpicEvent, TBookSegmentsEpicEvent, RootState, EpicDependencies>

const isOnPath = (state$: StateObservable<RootState>, fragment: string): boolean =>
  state$.value.router.location.pathname.includes(fragment)

export const maybeRefetchPortfolioSummariesOnTokenRefresh: BookSegmentsEpic = (action$, state$) =>
  action$.pipe(
    filter(isOfType(userApiEvents.refreshToken_success.type)),
    filter(() => isOnPath(state$, '/leases')),
    map(() => portfolioApiEvents.portfolioSummaries_request()),
    tag('module/bookSegments/maybeRefetchPortfolioSummariesOnTokenRefresh'),
  )

export const maybeRefetchDraftInvoicesOnTokenRefresh: BookSegmentsEpic = (action$, state$) =>
  action$.pipe(
    filter(isOfType(userApiEvents.refreshToken_success.type)),
    filter(() => isOnPath(state$, '/invoices/drafts')),
    map(() => invoiceApiEvents.invoices_request({ tags: '' })),
    tag('module/bookSegments/maybeRefetchDraftInvoicesOnTokenRefresh'),
  )

export const maybeRefetchActiveInvoicesOnTokenRefresh: BookSegmentsEpic = (action$, state$) =>
  action$.pipe(
    filter(isOfType(userApiEvents.refreshToken_success.type)),
    filter(() => isOnPath(state$, '/invoices/active')),
    mergeMap(() => reconApiConstants.invoiceStatuses.map(tags => reconApiEvents.invoices_request({ tags }))),
    tag('module/bookSegments/maybeRefetchActiveInvoicesOnTokenRefresh'),
  )

/**
 * Follow-up requests for a segment save. Each helper sends one request per id and
 * reports a failure against `initAction` without stopping the others.
 */
type TFollowUpContext = {
  state$: StateObservable<RootState>
  deps: EpicDependencies
  initAction: AnyAction
}

/** The segments a lease belongs to: always the default segment, plus `segmentId` if given */
const leaseSegments = (defaultSegmentId: string | null, segmentId?: string): string[] =>
  [defaultSegmentId, segmentId].filter((id): id is string => !!id)

const amendPortfolioSegments$ = (
  portfolioIds: string[],
  segments: string[],
  { state$, deps: { post, catchRestError }, initAction }: TFollowUpContext,
): Observable<TBookSegmentsEpicEvent> =>
  from(portfolioIds).pipe(
    mergeMap(id => {
      const body = { segments }
      /** @todo need to fetch portfolio segments and amend ids once we support portfolios in multiple segments */
      return post(PORTFOLIO_ENDPOINTS.AMEND_SEGMENTS, state$, body, { id }).pipe(
        map(() => portfolioApiEvents.amendSegments_success({ ...body, id })),
        catchRestError(initAction),
      )
    }),
  )

const addSegmentMemberships$ = (
  userIds: string[],
  segmentId: string,
  { state$, deps: { post, catchRestError }, initAction }: TFollowUpContext,
): Observable<TBookSegmentsEpicEvent> =>
  from(userIds).pipe(
    mergeMap(userId => {
      const params = { userId, segmentId }
      return post(USER_ENDPOINTS.SEGMENTS, state$, null, params).pipe(
        map(() => userApiEvents.addSegmentMembership_success(params)),
        catchRestError(initAction),
      )
    }),
  )

const deleteSegmentMemberships$ = (
  userIds: string[],
  segmentId: string,
  { state$, deps: { remove, catchRestError }, initAction }: TFollowUpContext,
): Observable<TBookSegmentsEpicEvent> =>
  from(userIds).pipe(
    mergeMap(userId => {
      const params = { userId, segmentId }
      return remove(USER_ENDPOINTS.SEGMENTS, state$, params).pipe(
        map(() => userApiEvents.deleteSegmentMembership_success(params)),
        catchRestError(initAction),
      )
    }),
  )

/**
 * Emit `saved`, then every follow-up event once all follow-ups have run, then
 * close the form and mark the save complete.
 */
const completeSave$ = (
  saved: TBookSegmentsEpicEvent,
  followUps: Observable<TBookSegmentsEpicEvent>[],
  done: TBookSegmentsEpicEvent,
): Observable<TBookSegmentsEpicEvent> => concat(of(saved), merge(...followUps), of(goBack(), done))

/**
 * Create a segment, add the chosen leases to it and give the chosen users access.
 * If creating the segment itself fails, nothing else is sent and the form stays open.
 */
export const createSegment: BookSegmentsEpic = (action$, state$, deps) =>
  action$.pipe(
    filter(isOfType(events.createSegment_request.type)),
    mergeMap(action => {
      const { name, portfolios, team } = action.payload
      const context = { state$, deps, initAction: action }
      const defaultSegmentId = getDefaultSegmentId(state$.value)

      return deps.post(AGENCY_ENDPOINTS.SEGMENT_BASE, state$, { name }, {}).pipe(
        pluck('response'),
        mergeMap((segments: TAgencySegment[]) => {
          // The endpoint answers with every segment on the agency, the new one last
          const segmentId = segments[segments.length - 1]?.id
          const followUps = segmentId
            ? [
                amendPortfolioSegments$(
                  portfolios.map(p => p.id),
                  leaseSegments(defaultSegmentId, segmentId),
                  context,
                ),
                addSegmentMemberships$(
                  team.map(m => m.id),
                  segmentId,
                  context,
                ),
              ]
            : []

          return completeSave$(
            agencyApiEvents.createSegment_success(segments),
            followUps,
            events.createSegment_success({ name }),
          )
        }),
        deps.catchRestError(action),
      )
    }),
    tag('module/bookSegments/createSegment'),
  )

/**
 * Rename a segment and sync its leases and users with the form. The default
 * segment can't be renamed (the API rejects it), so only its users are saved.
 */
export const updateSegment: BookSegmentsEpic = (action$, state$, deps) =>
  action$.pipe(
    filter(isOfType(events.updateSegment_request.type)),
    mergeMap(action => {
      const { id, name, portfolios, team, deletedPortfolios, deletedTeamMemberships } = action.payload
      const context = { state$, deps, initAction: action }
      const defaultSegmentId = getDefaultSegmentId(state$.value)
      const isDefault = id === defaultSegmentId

      const rename$: Observable<TAgencySegment[]> = isDefault
        ? of(agencyApiSelectors.getSegments(state$.value))
        : deps.put(AGENCY_ENDPOINTS.SEGMENT_ENTITY, state$, { name }, { segmentId: id }).pipe(pluck('response'))

      return rename$.pipe(
        mergeMap(segments =>
          completeSave$(
            agencyApiEvents.updateSegment_success(segments),
            [
              isDefault
                ? EMPTY
                : amendPortfolioSegments$(
                    portfolios.map(p => p.id),
                    leaseSegments(defaultSegmentId, id),
                    context,
                  ),
              amendPortfolioSegments$(deletedPortfolios, leaseSegments(defaultSegmentId), context),
              addSegmentMemberships$(
                team.map(m => m.id),
                id,
                context,
              ),
              deleteSegmentMemberships$(deletedTeamMemberships, id, context),
            ],
            events.updateSegment_success({ name }),
          ),
        ),
        deps.catchRestError(action),
      )
    }),
    tag('module/bookSegments/updateSegment'),
  )
